import React, { useContext } from "react";
import { mediaOutletUrl, toAbsoluteUrl } from "views_shared/app/javascript/utils/urls";
import ProfileImage from "../../ProfileImage";
import EntityHeader from "../../profiles/EntityHeader";
import FollowControl from "../../profiles/FollowControl";
import ShareProfileButton from "../../profiles/ShareProfileButton";
import { MediaOutletContext } from "../data/media_outlet_context_management";

const mediaOutletBannerImage = RAILS_ASSET_URL("/media-profile-banner.png");

// Media Outlet's engine header on the shared <EntityHeader> shell. Media Outlet has no cover
// upload feature of its own, so it reuses the Media Profile person page's bundled banner asset
// rather than shipping an outlet-specific one — same generated-default treatment as Product. Logo +
// display name + domain link, plus the curation affordances. Those are admin-only: `editable` is the
// outlet policy's #update?, and outlets are Qwoted-curated, so nobody else sees an edit control.
function MediaOutletHeader() {
  const { mediaOutlet, viewerContext, setOpenEditModal, silentReload } = useContext(MediaOutletContext);
  const editable = !!viewerContext?.editable;

  // Actions are Follow (viewers only — you don't follow a page you curate) + View Website + Share
  // Profile; no kebab on entity pages. The outlet has no separate "website" field — `domain` (a
  // bare hostname, see HeaderModalBody) is the same field the identity column already links, so it
  // renders-when-present here too rather than assuming every outlet has one on file.
  const websiteUrl = mediaOutlet.domain ? `https://${mediaOutlet.domain}` : null;
  const canonicalUrl = toAbsoluteUrl(mediaOutletUrl({ slug: mediaOutlet.slug, migrate_id: mediaOutlet.migrate_id }));

  const actions = (
    <>
      {!viewerContext?.isSelf && <FollowControl followableType="Id::MediaOutlet" followableId={mediaOutlet.id} />}
      {websiteUrl && (
        <a
          href={websiteUrl}
          className="btn btn-outline-primary text-nowrap d-block mb-2 me-2"
          target="_blank"
          rel="noopener noreferrer"
        >
          <i className="fa-solid fa-globe me-2" />
          View Website
        </a>
      )}
      <ShareProfileButton url={canonicalUrl} />
    </>
  );

  const banner = (
    <div className="person-header-banner">
      <img src={mediaOutletBannerImage} alt="" />
    </div>
  );

  // The framed entity-avatar variant the Company header uses: the logo is contained in its tile
  // rather than cropped to fill it, which matters for wordmarks. Editing is in-place over the logo
  // itself (hover for the replace/remove controls), the same interaction as the Company logo.
  const avatar = (
    <ProfileImage
      editable={editable}
      imgOwnerId={mediaOutlet.id}
      imgOwnerName={mediaOutlet.display_name}
      imgOwnerType="media_outlet"
      profileImageUrl={mediaOutlet.profile_image_url}
      onChange={silentReload}
      framed
    />
  );

  const identity = (
    <>
      {/* Owner controls live on the name line, not in the actions row: the pen is the same header
          edit affordance every person engine uses. The profile photo has no button here — it's
          edited in place over the logo itself, like the Company logo. */}
      <div className="d-flex flex-wrap align-items-center mb-1">
        <h1 className="h2 p-0 m-0">{mediaOutlet.display_name}</h1>
        {editable && (
          <button
            id="modal-button-HEADER"
            type="button"
            aria-label="Edit Header"
            className="btn btn-primary btn-sm ms-2 d-inline-flex align-items-center justify-content-center"
            style={{ width: "2rem", height: "2rem", padding: 0 }}
            onClick={() => setOpenEditModal("HEADER")}
          >
            <i className="fa-solid fa-pencil-alt" />
          </button>
        )}
      </div>
      {mediaOutlet.headline && <h2 className="h5 text-body-secondary mb-1">{mediaOutlet.headline}</h2>}
      {mediaOutlet.domain && (
        <div className="text-body-secondary">
          <a
            className="text-body-secondary"
            href={`https://${mediaOutlet.domain}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {mediaOutlet.domain}
          </a>
        </div>
      )}
    </>
  );

  return (
    <EntityHeader
      banner={banner}
      avatar={avatar}
      avatarWrapperClassName="media-outlet-avatar-frame"
      identity={identity}
      actions={actions}
    />
  );
}

export default MediaOutletHeader;
